import type { VendorUserRole } from 'library/constants'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { jwtDecode } from 'jwt-decode'

import { getAuthToken } from '@leanpay/services'
import { LoadingSpinner } from '@leanpay/ui'

import AppContext from './app-context'
import {
    useGetCurrency,
    useGetSessionInfo,
    useGetVendorRequestInfo,
} from 'library/hooks/query-hooks'

const AppStore = ({ children }: { children: ReactNode }) => {
    const isInStore = window.location.pathname === '/create-offer'
    const { data: vendorRequestInfoData } = useGetVendorRequestInfo(isInStore)

    const { data: currencyInfo, refetch: refetchCurrencyInfo } = useGetCurrency()
    const { data: sessionInfoData, isPending: sessionInfoLoading } = useGetSessionInfo()
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [progress, setProgress] = useState<number>(0)
    const [isAppLoading, setIsAppLoading] = useState(true)
    const [userRoles, setUserRoles] = useState<VendorUserRole[]>([])

    const contextValue = {
        isAuthenticated,
        progress,
        vendorRequestInfo: vendorRequestInfoData,
        currencyInfo,
        sessionInfoLoading,
        isAppLoading,
        userRoles,
        setIsAppLoading,
        setIsAuthenticated,
        setProgress,
        setUserRoles,
    }

    const handleSetUserRoles = (token: string) => {
        try {
            const decoded: { auth: string } = jwtDecode(token)
            const roles = decoded.auth.split(',')
            setUserRoles(roles as VendorUserRole[])
        } catch (error) {
            console.error('Failed to decode JWT:', error)
        }
    }

    useEffect(() => {
        const token = getAuthToken()

        if (token) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            handleSetUserRoles(token)
        }

        if (sessionInfoData) {
            setIsAuthenticated(true)
            void refetchCurrencyInfo()
        }

        if (!sessionInfoLoading) {
            setIsAppLoading(false)
        }
    }, [sessionInfoData, sessionInfoLoading])

    if (isAppLoading) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center">
                <LoadingSpinner />
            </div>
        )
    }

    return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
}

export default AppStore
